import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import {
  Badge, IconButton, InputAdornment, OutlinedInput, Menu, MenuItem, Typography, FormControl, InputLabel, Select, FormGroup, FormControlLabel, Checkbox, Popover,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import { useSelector, useDispatch } from 'react-redux';
import VehicleDataCard from '../common/components/VehicleDataCard';
import WhiteLogo from '../common/components/WhiteLogo';
import StatusCircle from '../common/components/StatusCircle';
import NavigationButton from '../common/components/NavigationButton';
import usePersistedState from '../common/util/usePersistedState';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import { useAdministrator, useDeviceReadonly } from '../common/util/permissions';
import useFilteredDevicesByVehicleName from './useFilteredDevicesByVehicleName';
import { vehicleMovementStatusActions } from '../store/vehicleMovementStatus';

const useStyles = makeStyles((theme) => ({
  main: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 1,
    height: '100%',
    maxWidth: '175px',
  },
  logo: {
    marginTop: '20px',
    minHeight: '51px',
  },
  scrollbarTrack: {
    '&::-webkit-scrollbar-track': {
      WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.2)',
      borderRadius: '10px',
      backgroundColor: 'transparent',
    },
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '10px',
    },
  },
  scrollbarThumb: {
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,.2)',
      backgroundColor: '#555',
    },
  },
  statusPanel: {
    margin: '20px 0',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
  },
  buttonsAndVehicles: {
    position: 'absolute',
    left: '200px',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  buttons: {
    display: 'flex',
    minHeight: '95px',
  },
  vehicles: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '15px',
    height: 'calc(100% - 95px)',
    overflowY: 'scroll',
  },
  search: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'white',

  },
  addButton: {
    width: 'calc(100% - 40px)',
  },
  white: {
    color: 'white',
  },
  accountMenu: {
    marginLeft: '18px',
    marginTop: '4px',
  },
  filterPanel: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    width: theme.dimensions.drawerWidthTablet,
    float: 'right',
  },
}));

const NewMainPanel = ({
  keyword,
  setKeyword,
  filterSort,
  setFilterSort,
  filterMap,
  setFilterMap,
}) => {
  const navigate = useNavigate();
  const [showVehicles, setShowVehicles] = useState(false);
  const [scrollbar, setScrollbar] = useState(false);
  const classes = useStyles();
  const t = useTranslation();
  const [filter, setFilter] = usePersistedState('filter', {
    statuses: [],
    groups: [],
  });
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [vehicleMovementStatus, setVehicleMovementStatus] = useState({
    running: { count: 0, ids: [] },
    stopped: { count: 0, ids: [] },
    idle: { count: 0, ids: [] },
    inactive: { count: 0, ids: [] },
    noData: { count: 0, ids: [] },
    total: { count: 0, ids: [] },
  });
  const [vehicles, setVehicles] = useState([]);
  const [latestPositions, setLatestPositions] = useState({});
  const user = useSelector((state) => state.session.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const positions = useSelector((state) => state.session.positions);
  const [devicesAnchorEl, setDevicesAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const inputRef = useRef();
  useFilteredDevicesByVehicleName(vehicles, keyword, filter, filterSort, setFilteredDevices);
  const admin = useAdministrator();
  const deviceReadonly = useDeviceReadonly();

  useEffect(() => {
    setLatestPositions(positions);
  }, [positions]);

  useEffect(() => {
    const currentVehicleIds = vehicles.map((v) => v.id);
    const runningIds = Object.values(latestPositions)
      .filter((p) => currentVehicleIds.includes(p.deviceId))
      .filter((p) => vehicles.find((v) => v.id === p.deviceId).status === 'online')
      .filter((p) => p.attributes.ignition === true)
      .map((p) => p.deviceId);
    const stoppedIds = Object.values(latestPositions)
      .filter((p) => currentVehicleIds.includes(p.deviceId))
      .filter((p) => vehicles.find((v) => v.id === p.deviceId).status === 'online')
      .filter((p) => p.speed === 0)
      .map((p) => p.deviceId);
    const idleIds = Object.values(latestPositions)
      .filter((p) => currentVehicleIds.includes(p.deviceId))
      .filter((p) => vehicles.find((v) => v.id === p.deviceId).status === 'online')
      .filter((p) => p.speed === 0 && p.attributes.ignition === true)
      .map((p) => p.deviceId);
    const inactiveIds = Object.values(latestPositions)
      .filter((p) => currentVehicleIds.includes(p.deviceId))
      .filter((p) => vehicles.find((v) => v.id === p.deviceId).status !== 'online')
      .map((p) => p.deviceId);
    const noDataIds = vehicles.filter((v) => !Object.values(latestPositions).map((p) => p.deviceId).includes(v.id)).map((v) => v.id);
    const result = {
      running: { count: runningIds.length, ids: runningIds, color: '#00eb00' },
      stopped: { count: stoppedIds.length, ids: stoppedIds, color: '#ff2828' },
      idle: { count: idleIds.length, ids: idleIds, color: '#ffc41f' },
      inactive: { count: inactiveIds.length, ids: inactiveIds, color: '#ffffff' },
      noData: { count: noDataIds.length, ids: noDataIds, color: '#676767' },
      total: { count: currentVehicleIds.length, ids: currentVehicleIds, color: '#676767' },
    };
    setVehicleMovementStatus(result);
    dispatch(vehicleMovementStatusActions.update(result));
  }, [vehicles, latestPositions]);

  useEffect(() => {
    const updatedVehicles = [];
    Object.values(latestPositions).forEach((p) => {
      const vehicle = vehicles.find((v) => v.id === p.deviceId);
      if (vehicle) {
        const updatedVehicle = { ...vehicle };
        updatedVehicle.ignition = p.attributes.ignition;
        updatedVehicle.speed = p.speed;
        updatedVehicle.fixTime = p.fixTime;
        updatedVehicles.push(updatedVehicle);
      }
      setVehicles(updatedVehicles);
    });
  }, [latestPositions]);

  const handleAccount = () => {
    setAnchorEl(null);
    navigate(`/settings/user/${user.id}`);
  };

  const handleLogout = async () => {
    setAnchorEl(null);

    const notificationToken = window.localStorage.getItem('notificationToken');
    if (notificationToken) {
      window.localStorage.removeItem('notificationToken');
      const tokens = user.attributes.notificationTokens?.split(',') || [];
      if (tokens.includes(notificationToken)) {
        const updatedUser = {
          ...user,
          attributes: {
            ...user.attributes,
            notificationTokens: tokens.length > 1 ? tokens.filter((it) => it !== notificationToken).join(',') : undefined,
          },
        };
        await fetch(`/api/users/${user.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedUser),
        });
      }
    }

    await fetch('/api/session', { method: 'DELETE' });
    nativePostMessage('logout');
    navigate('/login');
    dispatch(sessionActions.updateUser(null));
  };

  useEffectAsync(async () => {
    if (vehicles.length === 0) {
      const vehiclesResponse = await (await fetch('/api/devices/extended')).json();
      setVehicles(vehiclesResponse);
    }
    if (Object.values(latestPositions).length === 0) {
      const latestPositionsResponse = await (await fetch('/api/positions/latest')).json();
      setLatestPositions(latestPositionsResponse);
    }
  }, []);

  useEffect(() => {
    const element = document.getElementById('buttonsAndVehicles');
    if (element.offsetHeight >= element.parentElement.offsetHeight) {
      setScrollbar(true);
    } else {
      setScrollbar(false);
    }
  });

  const getFiteredVehicles = () => {
    const idList = filteredDevices.map((d) => d.uniqueId);
    const filteredVehicles = idList
      .filter((id) => vehicles.some((obj) => obj.uniqueId === id))
      .map((id) => vehicles.find((obj) => obj.uniqueId === id));
    return filteredVehicles;
  };

  const groups = useSelector((state) => state.groups.items);
  const devices = useSelector((state) => state.devices.items);
  const deviceStatusCount = (status) => Object.values(devices).filter((d) => d.status === status).length;

  return (
    <>
      <div className={classes.main}>
        <div className={classes.logo}>
          <WhiteLogo />
        </div>
        <div className={classes.statusPanel}>
          <StatusCircle color={vehicleMovementStatus.running.color} text="Running" number={vehicleMovementStatus.running.count} isTotal={false} />
          <StatusCircle color={vehicleMovementStatus.idle.color} text="Idle" number={vehicleMovementStatus.idle.count} isTotal={false} />
          <StatusCircle color={vehicleMovementStatus.stopped.color} text="Stopped" number={vehicleMovementStatus.stopped.count} isTotal={false} />
          <StatusCircle color={vehicleMovementStatus.inactive.color} text="Inactive" number={vehicleMovementStatus.inactive.count} isTotal={false} />
          <StatusCircle color={vehicleMovementStatus.noData.color} text="No Data" number={vehicleMovementStatus.noData.count} isTotal={false} />
          <StatusCircle color={vehicleMovementStatus.total.color} text="Total" number={vehicleMovementStatus.total.count} isTotal />
        </div>
      </div>
      <div id="buttonsAndVehicles" className={classes.buttonsAndVehicles} style={{ height: scrollbar && showVehicles ? '100%' : 'fit-content' }}>
        <div className={classes.buttons}>
          <NavigationButton icon="MenuIcon" text={showVehicles ? 'Hide Vehicle List' : 'Show Vehicle List'} onClick={() => setShowVehicles(!showVehicles)} />
          <NavigationButton icon="DescriptionIcon" text={t('reportTitle')} onClick={() => navigate('/reports/route')} />
          <NavigationButton icon="SettingsIcon" text={t('settingsTitle')} onClick={() => navigate('/settings/preferences')} />
          <NavigationButton icon="PersonIcon" text={t('settingsUser')} onClick={(event) => setAnchorEl(event.currentTarget)} />
          <Menu className={classes.accountMenu} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={handleAccount}>
              <Typography color="textPrimary">{t('settingsUser')}</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Typography color="error">{t('loginLogout')}</Typography>
            </MenuItem>
          </Menu>
          {/* <NavigationButton icon="EngineeringIcon" text="Legacy Page" onClick={() => navigate('/')} /> */}
        </div>
        {showVehicles && (
          <div className={`$${classes.scrollbarTrack} ${classes.scrollbar} ${classes.scrollbarThumb} ${classes.vehicles}`}>

            <div style={{ display: 'flex' }}>
              <OutlinedInput
                placeholder={t('sharedSearchDrivers')}
                className={admin ? `${classes.search} ${classes.addButton}` : `${classes.search}`}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onFocus={(e) => setDevicesAnchorEl(e.current)}
                onBlur={() => setDevicesAnchorEl(null)}
                ref={inputRef}
                endAdornment={(
                  <InputAdornment
                    position="end"
                    className={classes.white}
                  >
                    <IconButton
                      size="small"
                      edge="end"
                      className={classes.white}
                      onClick={() => setFilterAnchorEl(inputRef.current)}
                    >
                      <Badge color="info" variant="dot" invisible={!filter.statuses.length && !filter.groups.length}>
                        <TuneIcon fontSize="small" />
                      </Badge>
                    </IconButton>

                  </InputAdornment>
            )}
                size="small"
                fullWidth={!admin}
              />
              {
                admin && (
                <IconButton className={classes.white} edge="end" onClick={() => navigate('/settings/device')} disabled={deviceReadonly}>
                  <AddIcon />
                </IconButton>
                )
              }
            </div>

            <Popover
              open={!!filterAnchorEl}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <div className={classes.filterPanel}>
                <FormControl>
                  <InputLabel>{t('deviceStatus')}</InputLabel>
                  <Select
                    label={t('deviceStatus')}
                    value={filter.statuses}
                    onChange={(e) => setFilter({ ...filter, statuses: e.target.value })}
                    multiple
                  >
                    <MenuItem value="online">{`${t('deviceStatusOnline')} (${deviceStatusCount('online')})`}</MenuItem>
                    <MenuItem value="offline">{`${t('deviceStatusOffline')} (${deviceStatusCount('offline')})`}</MenuItem>
                    <MenuItem value="unknown">{`${t('deviceStatusUnknown')} (${deviceStatusCount('unknown')})`}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel>{t('settingsDeviceGroups')}</InputLabel>
                  <Select
                    label={t('settingsDeviceGroups')}
                    value={filter.groups}
                    onChange={(e) => setFilter({ ...filter, groups: e.target.value })}
                    multiple
                  >
                    {Object.values(groups).sort((a, b) => a.name.localeCompare(b.name)).map((group) => (
                      <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel>{t('sharedSortBy')}</InputLabel>
                  <Select
                    label={t('sharedSortBy')}
                    value={filterSort}
                    onChange={(e) => setFilterSort(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{'\u00a0'}</MenuItem>
                    <MenuItem value="driverName">{t('sharedName')}</MenuItem>
                    <MenuItem value="lastUpdate">{t('deviceLastUpdate')}</MenuItem>
                  </Select>
                </FormControl>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={filterMap} onChange={(e) => setFilterMap(e.target.checked)} />}
                    label={t('sharedFilterMap')}
                  />
                </FormGroup>
              </div>
            </Popover>
            {getFiteredVehicles().map((vehicle) => (
              <VehicleDataCard key={vehicle.id} data={vehicle} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default NewMainPanel;
